import { facilityAssetId, type FacilityRecord, type FacilityMap } from '@/features/catalogue/facilities';
import type { Antenna } from '@/features/loader/types';
import type { AssetSpec, Element, ProceduralFacilitySpec, Subsystem } from '../types';
import { antennaAssetId, proceduralSpecForAntenna } from './antennaSpec';
import { facilityInterior, roomPose } from './facilityInteriors';

export function assetSpecForFacility(
  f: FacilityRecord,
  antennas: { network: string; antenna: Antenna }[],
  map: FacilityMap | null,
): AssetSpec {
  const interior = facilityInterior(f.id);
  const extentM = Math.max(f.extentM, map?.coverage?.extentM ?? 0);
  const procedural: ProceduralFacilitySpec = {
    kind: 'facility',
    facilityId: f.id,
    lat: f.lat,
    lon: f.lon,
    extentM,
    interior,
    mappingNote: map?.coverage
      ? `${map.coverage.displayedBuildings} building footprints · ${map.coverage.locationQuality}. ${map.coverage.note}`
      : undefined,
    terminals: antennas.map(({ network, antenna: a }, i) => {
      const model = proceduralSpecForAntenna(a);
      return {
        id: `terminal_${i}`,
        assetId: antennaAssetId(a.id, network),
        label: a.name,
        lat: Number(a.location.lat),
        lon: Number(a.location.lon),
        diameterM: model.diameterM,
        rig: model.rig,
        radome: model.radome ?? false,
        bands: a.capabilities.bands,
      };
    }),
    features: (map?.features ?? [])
      .filter((feature) => feature.kind === 'building' && feature.coordinates.length >= 4)
      .filter((feature) =>
        feature.display !== undefined
          ? feature.display
          : feature.coordinates.some(
              ([lon, lat]) =>
                Math.hypot((lon - f.lon) * Math.cos((f.lat * Math.PI) / 180), lat - f.lat) * 111320 <
                Math.max(250, f.extentM + 100),
            ),
      )
      .slice(0, 120),
  };
  const distance = Math.max(320, extentM * 2.5);
  return {
    id: facilityAssetId(f.id),
    elementId: facilityAssetId(f.id),
    label: f.name,
    modelNote: `${procedural.features.length ? 'Mapped site context; representative equipment.' : 'Building footprints unavailable.'} ${interior ? 'Illustrative interiors. ' : ''}No live telemetry.`,
    procedural,
    poses: {
      main: { target: [0, 0, 0], position: [-distance * 0.5, distance * 0.6, distance * 0.62], fov: 45 },
    },
    bindings: [
      ...procedural.terminals.map((t) => ({ subsystemId: t.id, nodes: [t.id], label: t.label })),
      ...procedural.features.map((b, i) => ({
        subsystemId: `building_${b.id}`,
        nodes: [`building_${b.id}`],
        label:
          b.tags.name ?? `Building ${i + 1}${b.id === interior?.buildingId ? ' · interior showcase' : ''}`,
      })),
      ...(interior?.rooms.flatMap((room) => [
        {
          subsystemId: room.id,
          nodes: [room.id],
          label: room.label,
          parentId: `building_${interior.buildingId}`,
          pose: roomPose(interior, room),
        },
        ...room.equipment.map((e) => ({
          subsystemId: e.id,
          nodes: [e.id],
          label: e.label,
          parentId: room.id,
        })),
      ]) ?? []),
    ],
    signalPaths: interior
      ? [
          {
            id: 'facility-flow',
            label: 'Illustrative data flow',
            direction: 'receive',
            visibleInSolid: true,
            colour: '#36e8dc',
            subsystemIds:
              f.id === 'wallops-cdas'
                ? [
                    'terminal_0',
                    'receive_equipment_0',
                    'receive_equipment_1',
                    'receive_equipment_2',
                    'transport_equipment_0',
                    'transport_equipment_1',
                  ]
                : [
                    'ingest_equipment_0',
                    'ingest_equipment_1',
                    'processing_equipment_0',
                    'processing_equipment_1',
                    'distribution_equipment_0',
                    'distribution_equipment_1',
                  ],
          },
        ]
      : undefined,
    site: {
      facilityId: f.id,
      lat: f.lat,
      lon: f.lon,
      headingDeg: 0,
      extentM,
      location: f.location,
      sourceUrl: f.sourceUrl,
      sourceTitle: f.sourceTitle,
      mapUrl: map ? f.mapUrl : undefined,
      description: `${f.operator}. ${f.role}. ${f.note}`,
    },
  };
}

/** Reference specifications never enter the reading/health pipeline. */
export function facilityElement(f: FacilityRecord, spec: AssetSpec): Element {
  const model = spec.procedural as ProceduralFacilitySpec;
  const subsystems: Subsystem[] = model.terminals.map((t) => ({
    id: t.id,
    label: t.label,
    data: [],
    modelOnly: true,
    reference: {
      sourceTitle: f.sourceTitle,
      sourceUrl: f.sourceUrl,
      checkedAt: f.checkedAt,
      note: 'Planning catalogue specifications; model shape and exact installed location require verification. No live readings.',
      values: [
        { label: 'Diameter', value: `${t.diameterM} m` },
        { label: 'Bands', value: t.bands.join(', ') },
        { label: 'Location', value: `${t.lat.toFixed(6)}, ${t.lon.toFixed(6)}` },
      ],
    },
  }));
  subsystems.push(
    ...model.features.map((b, i) => ({
      id: `building_${b.id}`,
      label:
        b.tags.name ??
        `Building ${i + 1}${b.id === model.interior?.buildingId ? ' · interior showcase' : ''}`,
      modelOnly: true,
      data: [],
      reference: {
        sourceTitle: `${b.source?.provider ?? 'OpenStreetMap'} footprint`,
        sourceUrl: b.source?.url ?? `https://www.openstreetmap.org/way/${b.id}`,
        checkedAt: b.source?.retrievedAt ?? f.checkedAt,
        note: 'Building use is not assigned. Heights use map tags where available, otherwise an illustrative 3.5 m per storey; roof and facade details are representative.',
        values: [
          { label: 'Geometry basis', value: 'Public mapped footprint' },
          { label: 'Source feature', value: String(b.id) },
          {
            label: 'Height',
            value: b.tags.height
              ? `${b.tags.height} m (source attribute)`
              : b.tags['building:levels']
                ? `${b.tags['building:levels']} mapped storeys; height estimated`
                : 'Illustrative 3.5 m',
          },
          {
            label: 'Building use',
            value: 'Unverified; nearby footprint does not establish facility ownership',
          },
        ],
      },
    })),
  );
  if (model.interior) {
    const interior = model.interior;
    const reference = (note: string) => ({
      basis: 'illustrative' as const,
      sourceTitle: interior.sourceTitle,
      sourceUrl: interior.sourceUrl,
      checkedAt: '2026-10-16',
      note: 'Illustrative interior fitted inside a mapped building. Room allocation, rack counts, equipment models and cable routes are not verified as-built details. No live telemetry.',
      values: [
        { label: 'Function', value: note },
        { label: 'Layout basis', value: 'Representative demonstration; not a floor plan' },
      ],
    });
    for (const room of [...interior.rooms].reverse())
      subsystems.unshift({
        id: room.id,
        label: room.label,
        modelOnly: true,
        data: [],
        reference: reference(room.purpose),
        childs: room.equipment.map((e) => ({
          id: e.id,
          label: e.label,
          modelOnly: true,
          data: [],
          reference: reference(e.function),
        })),
      });
  }
  return {
    id: spec.id,
    callsign: f.name,
    type: 'Facility reference',
    subsystems,
    documents: [{ type: 'webpage', label: f.sourceTitle, href: f.sourceUrl }],
  };
}
